import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { API_BASE_URL } from "../../config";
import Loader from "../../Loader/loader";
import {
  Card,
  Input,
  Button,
  Progress,
  Steps,
  Checkbox,
  Tooltip,
  Space,
  Typography,
  Alert,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  LockOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CopyOutlined,
  SafetyOutlined,
  FileProtectOutlined,
  CheckCircleOutlined,
  BankOutlined,
  IdcardOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { fetchStates } from "../services/orderService";
import { COUNTRY_CODES, isValidPhone, withCountryCode } from "../utils/phones";
import { authHeaders,getSessionToken } from "../utils/session";

const { Text } = Typography;
const { Step } = Steps;

const AddUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [token] = useState(getSessionToken());

  const [formData, setFormData] = useState({
    name: searchParams.get("name") || "",
    email: searchParams.get("email") || "",
    mobile: searchParams.get("phone") || "",
    // Comes along when a lead is converted; +91 for a customer typed in by hand.
    country_code: searchParams.get("country_code") || "+91",
    password: "",
    address: searchParams.get("address") || "",
    state_name: "",
    pincode: "",
    gst_inclusion: false,
    company_name: searchParams.get("company") || "",
    gstNumber: "",
  });

  /* Carried from the lead that is being converted (Create Subscription).
     lead_id lets the backend close the RIGHT lead when the order is created —
     email/phone would match several leads. Attribution rides along so it is not
     lost the moment the lead becomes a customer. */
  const leadId = searchParams.get("lead_id") || "";
  /* Every number the lead carried. Only used to offer a choice — when this page
     is opened directly (no lead), the list is empty and nothing changes. */
  const leadPhones = [
    searchParams.get("phone") || "",
    ...(searchParams.get("additional_phone") || "").split(","),
  ]
    .map((v) => v.trim())
    .filter((v, i, arr) => v && arr.indexOf(v) === i);
  const leadUtm = {
    utm_source: searchParams.get("utm_source") || null,
    utm_medium: searchParams.get("utm_medium") || null,
    utm_campaign: searchParams.get("utm_campaign") || null,
  };

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Fetch States using React Query
  const { data: statesData = [], isLoading: statesLoading } = useQuery({
    queryKey: ["states"],
    queryFn: fetchStates,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  /* ================= PASSWORD STRENGTH ================= */
  useEffect(() => {
    if (formData.password) {
      let strength = 0;
      if (formData.password.length >= 6) strength += 25;
      if (formData.password.length >= 10) strength += 25;
      if (/[A-Z]/.test(formData.password)) strength += 15;
      if (/[a-z]/.test(formData.password)) strength += 15;
      if (/[0-9]/.test(formData.password)) strength += 10;
      if (/[^A-Za-z0-9]/.test(formData.password)) strength += 10;
      setPasswordStrength(Math.min(strength, 100));
    } else {
      setPasswordStrength(0);
    }
  }, [formData.password]);

  /* ================= VALIDATION ================= */
  const validateForm = () => {
    let newErrors = {};

    if (!formData.name || formData.name.length < 3) {
      newErrors.name = "Name must be at least 3 characters";
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Enter valid email address";
    }

    // 4–15 digits (E.164). The old [6-9]\d{9} rule rejected every foreign number.
    if (!isValidPhone(formData.mobile)) {
      newErrors.mobile = "Enter a valid number (4–15 digits, no country code)";
    }

    if (!formData.password || formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.address) {
      newErrors.address = "Address is required";
    }

    if (!formData.state_name) {
      newErrors.state_name = "State is required";
    }

    if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = "Enter valid 6-digit pincode";
    }

    if (formData.gst_inclusion) {
      if (!formData.company_name) {
        newErrors.company_name = "Company name required";
      }
      if (!formData.gstNumber) {
        newErrors.gstNumber = "GST number is required";
      } else if (formData.gstNumber.length !== 15) {
        newErrors.gstNumber = "GSTIN must be exactly 15 characters";
      } else if (!/^\d{2}[A-Z]{5}\d{4}[A-Z]\d[A-Z]\w$/.test(formData.gstNumber)) {
        newErrors.gstNumber = "Invalid GSTIN format. Expected: 27ABCDE1234F2Z5";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ================= HANDLE INPUT ================= */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  /* ================= GENERATE PASSWORD ================= */
  const generatePassword = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$!";
    let newPass = "";
    for (let i = 0; i < 12; i++) {
      newPass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password: newPass });
    toast.success("Password generated successfully!", { autoClose: 2000 });
  };

  /* ================= COPY PASSWORD ================= */
  const copyPassword = () => {
    navigator.clipboard.writeText(formData.password);
    toast.success("Password copied to clipboard!", { autoClose: 2000 });
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.warning("Please fix validation errors");
      return;
    }

    try {
      setIsLoading(true);

      const payload = {
        user_id: id,
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        country_code: formData.country_code || "+91",
        password: formData.password,
        user_type: "customer",
        address: formData.address,
        state: String(formData.state_name),
        pincode: formData.pincode,
        gst_inclusion: formData.gst_inclusion ? 1 : 0,
        company_name: formData.company_name,
        gst: formData.gstNumber,
        ...(leadId ? { lead_id: Number(leadId) } : {}),
        ...leadUtm,
      };

      const response = await axios.post(
        `${API_BASE_URL}/add-customer`,
        payload,
        {
          headers: { ...authHeaders() },
        },
      );

      // console.log(response);

      if (response.data.status === 200) {
        toast.success("Customer Added Successfully");
        const newUserId =
          response.data?.data?.user_id || response.data?.user_id;
        navigate(
          `/cms/manage-customer/create-order?customer_id=${newUserId}` +
            (leadId ? `&lead_id=${leadId}` : ""),
        );
      } else{
         const backendErrors = response.data.errors;
        const formattedErrors = {};

        Object.keys(backendErrors).forEach((key) => {
          formattedErrors[key] = backendErrors[key][0];
        });

        setErrors(formattedErrors);

        toast.warning("Please fix the highlighted errors");
      }
    } catch (error) {
      const backendErrors = error.response?.data?.errors;
      if (backendErrors) {
        const formattedErrors = {};
        Object.keys(backendErrors).forEach((key) => {
          formattedErrors[key] = backendErrors[key][0];
        });
        setErrors(formattedErrors);
        toast.warning("Please fix the highlighted errors");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally{
      setIsLoading(false);
    }
  };

  /* ================= PASSWORD STRENGTH COLOR ================= */
  const getPasswordStrengthColor = () => {
    if (passwordStrength < 40) return "#ff4d4f";
    if (passwordStrength < 70) return "#faad14";
    return "#52c41a";
  };

  const getPasswordStrengthStatus = () => {
    if (passwordStrength < 40) return "exception";
    if (passwordStrength < 70) return "normal";
    return "success";
  };

  /* ================= CUSTOM SELECT STYLES ================= */
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: errors.state_name
        ? "#ff4d4f"
        : state.isFocused
          ? "#1890ff"
          : "#d9d9d9",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(24, 144, 255, 0.2)" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#1890ff" : "#40a9ff",
      },
      minHeight: "40px",
      transition: "all 0.3s",
      borderRadius: "6px",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#1890ff"
        : state.isFocused
          ? "#e6f7ff"
          : "white",
      color: state.isSelected ? "white" : "#262626",
      "&:active": {
        backgroundColor: "#1890ff",
      },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  /* ================= INLINE STYLES ================= */
  const styles = {
    pageHeader: {
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      borderRadius: "16px",
      padding: "32px",
      marginBottom: "32px",
      boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
    },
    headerContent: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "20px",
    },
    iconWrapper: {
      width: "70px",
      height: "70px",
      background: "rgba(255, 255, 255, 0.25)",
      backdropFilter: "blur(10px)",
      borderRadius: "16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "32px",
      color: "white",
      boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
    },
    titleSection: {
      display: "flex",
      alignItems: "center",
      gap: "20px",
    },
    pageTitle: {
      color: "white",
      margin: 0,
      fontSize: "28px",
      fontWeight: "700",
      letterSpacing: "-0.5px",
    },
    pageSubtitle: {
      color: "rgba(255, 255, 255, 0.9)",
      margin: "4px 0 0 0",
      fontSize: "15px",
    },
    breadcrumbWrapper: {
      background: "rgba(255, 255, 255, 0.2)",
      backdropFilter: "blur(10px)",
      padding: "12px 20px",
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "14px",
      color: "white",
    },
    breadcrumbLink: {
      color: "white",
      textDecoration: "none",
      transition: "all 0.3s",
      padding: "4px 8px",
      borderRadius: "6px",
    },
    mainCard: {
      borderRadius: "20px",
      boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
      border: "none",
      overflow: "hidden",
    },
    sectionCard: {
      background: "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
      border: "2px solid #e8ecff",
      borderRadius: "16px",
      marginBottom: "24px",
      borderLeft: "4px solid #667eea",
      transition: "all 0.3s ease",
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      marginBottom: "20px",
      paddingBottom: "16px",
      borderBottom: "2px solid rgba(102, 126, 234, 0.15)",
    },
    sectionIcon: {
      width: "48px",
      height: "48px",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      fontSize: "24px",
      boxShadow: "0 6px 16px rgba(102, 126, 234, 0.3)",
    },
    sectionTitle: {
      margin: 0,
      fontSize: "20px",
      fontWeight: "700",
      color: "#262626",
    },
    sectionSubtitle: {
      margin: "4px 0 0 0",
      fontSize: "14px",
      color: "#8c8c8c",
    },
    inputLabel: {
      fontWeight: 600,
      color: "#262626",
      fontSize: "14px",
      marginBottom: "8px",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    strengthBar: {
      marginTop: "12px",
    },
    gstCheckbox: {
      background: "white",
      border: "2px solid #d9d9d9",
      borderRadius: "12px",
      padding: "20px",
      marginBottom: "20px",
      transition: "all 0.3s",
    },
    gstCheckboxChecked: {
      background: "linear-gradient(135deg, #f0f4ff 0%, #fafbff 100%)",
      border: "2px solid #667eea",
      boxShadow: "0 4px 16px rgba(102, 126, 234, 0.15)",
    },
    gstContent: {
      marginLeft: "32px",
    },
    gstTitle: {
      fontWeight: 600,
      fontSize: "16px",
      color: "#262626",
      marginBottom: "4px",
    },
    gstDescription: {
      fontSize: "13px",
      color: "#8c8c8c",
      margin: 0,
    },
    actionButtons: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "12px",
      paddingTop: "24px",
      borderTop: "2px solid #f0f0f0",
      marginTop: "32px",
    },
    stepsWrapper: {
      padding: "32px 0",
      maxWidth: "600px",
      margin: "0 auto",
    },
  };

  /* ================= UI ================= */
  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          {/* Header Section */}
          <div style={styles.pageHeader}>
            <div style={styles.headerContent}>
              <div style={styles.titleSection}>
                <div style={styles.iconWrapper}>
                  <UserOutlined />
                </div>
                <div>
                  <h4 style={styles.pageTitle}>Add New Customer</h4>
                  <p style={styles.pageSubtitle}>
                    Create a new customer account with complete details
                  </p>
                </div>
              </div>
              <div style={styles.breadcrumbWrapper}>
                <Link to="/cms/manage-customer" style={styles.breadcrumbLink}>
                  Customers
                </Link>
                <span style={{ color: "rgba(255,255,255,0.6)" }}>›</span>
                <span style={{ fontWeight: 600 }}>Add Customer</span>
              </div>
            </div>
          </div>

          {isLoading && <Loader />}

          {/* Progress Steps */}
          <div style={styles.stepsWrapper}>
            <Steps current={2} size="small">
              <Step title="Basic Info" icon={<UserOutlined />} />
              <Step title="Location" icon={<EnvironmentOutlined />} />
              <Step title="Security" icon={<SafetyOutlined />} />
              <Step
                title="Create Subscription"
                icon={<FileProtectOutlined />}
              />
            </Steps>
          </div>

          {/* Main Form Card */}
          <Card style={styles.mainCard}>
            <form onSubmit={handleSubmit}>
              {/* BASIC DETAILS */}
              <div style={styles.sectionCard}>
                <div style={{ padding: "24px" }}>
                  <div style={styles.sectionHeader}>
                    <div style={styles.sectionIcon}>
                      <UserOutlined />
                    </div>
                    <div>
                      <h5 style={styles.sectionTitle}>Basic Details</h5>
                      <p style={styles.sectionSubtitle}>
                        Enter customer's personal information
                      </p>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div style={styles.inputLabel}>
                        <UserOutlined /> Full Name{" "}
                        <span style={{ color: "#ff4d4f" }}>*</span>
                      </div>
                      <Input
                        size="large"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter full name"
                        prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                        status={errors.name ? "error" : ""}
                      />
                      {errors.name && (
                        <Text type="danger" style={{ fontSize: "12px" }}>
                          {errors.name}
                        </Text>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <div style={styles.inputLabel}>
                        <MailOutlined /> Email Address{" "}
                        <span style={{ color: "#ff4d4f" }}>*</span>
                      </div>
                      <Input
                        size="large"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="customer@example.com"
                        prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
                        status={errors.email ? "error" : ""}
                        autoComplete="off"
                      />
                      {errors.email && (
                        <Text type="danger" style={{ fontSize: "12px" }}>
                          {errors.email}
                        </Text>
                      )}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div style={styles.inputLabel}>
                        <PhoneOutlined /> Mobile Number{" "}
                        <span style={{ color: "#ff4d4f" }}>*</span>
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <div style={{ width: 150, flexShrink: 0 }}>
                          <Select
                            options={COUNTRY_CODES.map((c) => ({
                              value: c.code,
                              label: `${c.flag} ${c.code}`,
                              search: `${c.code} ${c.label}`,
                            }))}
                            value={COUNTRY_CODES.filter((c) => c.code === formData.country_code).map((c) => ({
                              value: c.code,
                              label: `${c.flag} ${c.code}`,
                            }))[0]}
                            onChange={(opt) =>
                              setFormData((prev) => ({ ...prev, country_code: opt?.value || "+91" }))
                            }
                            filterOption={(opt, input) =>
                              !input ||
                              String(opt.data.search || opt.label).toLowerCase().includes(input.toLowerCase())
                            }
                            styles={customSelectStyles}
                            placeholder="Code"
                          />
                        </div>
                        <Input
                          size="large"
                          name="mobile"
                          value={formData.mobile}
                          onChange={handleInputChange}
                          placeholder="Number without country code"
                          prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
                          maxLength={15}
                          status={errors.mobile ? "error" : ""}
                        />
                      </div>
                      {errors.mobile && (
                        <Text type="danger" style={{ fontSize: "12px" }}>
                          {errors.mobile}
                        </Text>
                      )}
                      {/* The lead had more than one number — let the user say
                          which one belongs to the customer instead of silently
                          taking the primary. */}
                      {leadPhones.length > 1 && (
                        <div style={{ marginTop: "10px" }}>
                          <div style={{ fontSize: "12px", color: "#8c8c8c", marginBottom: "6px" }}>
                            Numbers on this lead — click to use:
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                            {leadPhones.map((num) => {
                              const active = formData.mobile === num;
                              return (
                                <button
                                  key={num}
                                  type="button"
                                  onClick={() => {
                                    setFormData((prev) => ({ ...prev, mobile: num }));
                                    setErrors((prev) => ({ ...prev, mobile: "" }));
                                  }}
                                  style={{
                                    padding: "5px 12px",
                                    borderRadius: "20px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    border: `1px solid ${active ? "#667eea" : "#d9d9d9"}`,
                                    background: active ? "#667eea" : "#fff",
                                    color: active ? "#fff" : "#595959",
                                  }}
                                >
                                  {num}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* LOCATION DETAILS */}
              <div style={styles.sectionCard}>
                <div style={{ padding: "24px" }}>
                  <div style={styles.sectionHeader}>
                    <div style={styles.sectionIcon}>
                      <EnvironmentOutlined />
                    </div>
                    <div>
                      <h5 style={styles.sectionTitle}>Location Details</h5>
                      <p style={styles.sectionSubtitle}>
                        Provide address and location information
                      </p>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12 mb-3">
                      <div style={styles.inputLabel}>
                        <HomeOutlined /> Address{" "}
                        <span style={{ color: "#ff4d4f" }}>*</span>
                      </div>
                      <Input.TextArea
                        size="large"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Street address, apartment, suite, etc."
                        rows={3}
                        status={errors.address ? "error" : ""}
                      />
                      {errors.address && (
                        <Text type="danger" style={{ fontSize: "12px" }}>
                          {errors.address}
                        </Text>
                      )}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div style={styles.inputLabel}>
                        <EnvironmentOutlined /> State{" "}
                        <span style={{ color: "#ff4d4f" }}>*</span>
                      </div>
                      <Select
                        options={statesData}
                        value={statesData.find(
                          (s) => s.value === formData.state_name,
                        )}
                        onChange={(selected) => {
                          setFormData({
                            ...formData,
                            state_name: selected.value,
                          });
                          setErrors({ ...errors, state_name: "" });
                        }}
                        styles={customSelectStyles}
                        placeholder="Select state..."
                        isLoading={statesLoading}
                        isSearchable
                        className="w-100"
                      />
                      {errors.state_name && (
                        <Text
                          type="danger"
                          style={{
                            fontSize: "12px",
                            display: "block",
                            marginTop: "4px",
                          }}
                        >
                          {errors.state_name}
                        </Text>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <div style={styles.inputLabel}>
                        <EnvironmentOutlined /> Pincode{" "}
                        <span style={{ color: "#ff4d4f" }}>*</span>
                      </div>
                      <Input
                        size="large"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleInputChange}
                        placeholder="6-digit pincode"
                        prefix={
                          <EnvironmentOutlined style={{ color: "#bfbfbf" }} />
                        }
                        maxLength={6}
                        status={errors.pincode ? "error" : ""}
                      />
                      {errors.pincode && (
                        <Text type="danger" style={{ fontSize: "12px" }}>
                          {errors.pincode}
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* SECURITY */}
              <div style={styles.sectionCard}>
                <div style={{ padding: "24px" }}>
                  <div style={styles.sectionHeader}>
                    <div style={styles.sectionIcon}>
                      <SafetyOutlined />
                    </div>
                    <div>
                      <h5 style={styles.sectionTitle}>Security Settings</h5>
                      <p style={styles.sectionSubtitle}>
                        Set up account password and security
                      </p>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div style={styles.inputLabel}>
                        <LockOutlined /> Password{" "}
                        <span style={{ color: "#ff4d4f" }}>*</span>
                      </div>
                      <Input.Password
                        size="large"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Enter password"
                        prefix={<LockOutlined style={{ color: "#bfbfbf" }} />}
                        iconRender={(visible) =>
                          visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                        }
                        status={errors.password ? "error" : ""}
                        autoComplete="new-password"
                      />
                      {errors.password && (
                        <Text type="danger" style={{ fontSize: "12px" }}>
                          {errors.password}
                        </Text>
                      )}
                      {formData.password && (
                        <div style={styles.strengthBar}>
                          <Progress
                            percent={passwordStrength}
                            strokeColor={getPasswordStrengthColor()}
                            status={getPasswordStrengthStatus()}
                            size="small"
                            format={(percent) =>
                              percent < 40
                                ? "Weak"
                                : percent < 70
                                  ? "Medium"
                                  : "Strong"
                            }
                          />
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <div style={styles.inputLabel}>Quick Actions</div>
                      <Space.Compact style={{ width: "100%" }}>
                        <Tooltip title="Generate secure password">
                          <Button
                            size="large"
                            type="primary"
                            icon={<ThunderboltOutlined />}
                            onClick={generatePassword}
                            style={{
                              flex: 1,
                              background:
                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              border: "none",
                            }}
                          >
                            Generate Password
                          </Button>
                        </Tooltip>
                        <Tooltip title="Copy password">
                          <Button
                            size="large"
                            icon={<CopyOutlined />}
                            onClick={copyPassword}
                            disabled={!formData.password}
                          />
                        </Tooltip>
                      </Space.Compact>

                      <Alert
                        message="Use a strong password with 8+ characters"
                        type="info"
                        showIcon
                        style={{ marginTop: "12px", fontSize: "12px" }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* GST DETAILS */}
              <div style={styles.sectionCard}>
                <div style={{ padding: "24px" }}>
                  <div style={styles.sectionHeader}>
                    <div style={styles.sectionIcon}>
                      <FileProtectOutlined />
                    </div>
                    <div>
                      <h5 style={styles.sectionTitle}>GST Registration</h5>
                      <p style={styles.sectionSubtitle}>
                        Optional GST and company details
                      </p>
                    </div>
                  </div>

                  <div
                    style={{
                      ...styles.gstCheckbox,
                      ...(formData.gst_inclusion
                        ? styles.gstCheckboxChecked
                        : {}),
                    }}
                  >
                    <Checkbox
                      checked={formData.gst_inclusion}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          gst_inclusion: e.target.checked,
                        })
                      }
                      style={{ fontSize: "16px" }}
                    >
                      <div style={styles.gstContent}>
                        <div style={styles.gstTitle}>
                          <CheckCircleOutlined style={{ marginRight: "8px" }} />
                          Customer has GST Registration
                        </div>
                        <p style={styles.gstDescription}>
                          Enable this if the customer is registered for GST
                        </p>
                      </div>
                    </Checkbox>
                  </div>

                  {formData.gst_inclusion && (
                    <div style={{ marginTop: "20px" }}>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <div style={styles.inputLabel}>
                            <BankOutlined /> Company Name{" "}
                            <span style={{ color: "#ff4d4f" }}>*</span>
                          </div>
                          <Input
                            size="large"
                            name="company_name"
                            value={formData.company_name}
                            onChange={handleInputChange}
                            placeholder="Registered company name"
                            prefix={
                              <BankOutlined style={{ color: "#bfbfbf" }} />
                            }
                            status={errors.company_name ? "error" : ""}
                          />
                          {errors.company_name && (
                            <Text type="danger" style={{ fontSize: "12px" }}>
                              {errors.company_name}
                            </Text>
                          )}
                        </div>

                       <div className="col-md-6 mb-3">
                          <div style={styles.inputLabel}>
                            <IdcardOutlined /> GST Number{" "}
                            <span style={{ color: "#ff4d4f" }}>*</span>
                          </div>
                          <Input
                            size="large"
                            name="gstNumber"
                            value={formData.gstNumber}
                            onChange={(e) => {
                              const val = e.target.value.toUpperCase();
                              setFormData({ ...formData, gstNumber: val });
                              if (errors.gstNumber) {
                                setErrors({ ...errors, gstNumber: "" });
                              }
                            }}
                            placeholder="e.g. 27ABCDE1234F2Z5"
                            prefix={
                              <IdcardOutlined style={{ color: "#bfbfbf" }} />
                            }
                            maxLength={15}
                            status={
                              errors.gstNumber ||
                              (formData.gstNumber.length === 15 &&
                                !/^\d{2}[A-Z]{5}\d{4}[A-Z]\d[A-Z]\w$/.test(formData.gstNumber))
                                ? "error"
                                : ""
                            }
                          />
                          {errors.gstNumber && (
                            <Text type="danger" style={{ fontSize: "12px" }}>
                              {errors.gstNumber}
                            </Text>
                          )}
                          {!errors.gstNumber &&
                            formData.gstNumber &&
                            formData.gstNumber.length === 15 &&
                            !/^\d{2}[A-Z]{5}\d{4}[A-Z]\d[A-Z]\w$/.test(formData.gstNumber) && (
                              <Text type="danger" style={{ fontSize: "12px", display: "block", marginTop: "4px" }}>
                                Invalid GSTIN format. Expected: 27ABCDE1234F2Z5
                              </Text>
                            )}
                          {formData.gstNumber &&
                            formData.gstNumber.length > 0 &&
                            formData.gstNumber.length < 15 && (
                              <Text
                                style={{
                                  fontSize: "12px",
                                  display: "block",
                                  marginTop: "4px",
                                  color: "#faad14",
                                }}
                              >
                                GSTIN must be exactly 15 characters ({formData.gstNumber.length}/15)
                              </Text>
                            )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={styles.actionButtons}>
                <Button
                  size="large"
                  onClick={() => navigate("/cms/manage-customer")}
                  style={{ minWidth: "120px" }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="large"
                  htmlType="submit"
                  loading={isLoading}
                  icon={<CheckCircleOutlined />}
                  style={{
                    minWidth: "180px",
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    border: "none",
                    fontWeight: 600,
                  }}
                >
                  Add Customer
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AddUser;
